import { useAuth } from "@/hooks/AuthContext";
import React, { useEffect, useMemo, useState } from "react";
import api from "@/api/axios";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { API, fmtDate, ym } from "@/App";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, PlusCircle } from "lucide-react";

// Shadcn component imports
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { addDays, endOfMonth, endOfWeek, format, isBefore, startOfWeek, startOfMonth } from "date-fns";
import BigCalendar from "@/components/calendar/BigCalendar";
import TimeAttendancePage from "./TimeAttendancePage";



const getMonthWeeks = (monthStr) => {
  const [year, month] = monthStr.split("-").map(Number);
  const firstDay = startOfMonth(new Date(year, month - 1));
  const lastDay = endOfMonth(new Date(year, month - 1));

  let weeks = [];
  let current = startOfWeek(firstDay, { weekStartsOn: 1 });
  while (isBefore(current, lastDay) || current.getTime() === lastDay.getTime()) {
    const start = format(current, "yyyy-MM-dd");
    const end = format(endOfWeek(current, { weekStartsOn: 1 }), "yyyy-MM-dd");
    weeks.push({ start, end });
    current = addDays(current, 7);
  }
  return weeks;
};

function CalendarPage({ month, setMonth, isLeaveDialogOpen, setIsLeaveDialogOpen, googleRefreshKey }) {
  const { user } = useAuth();
  // const [month, setMonth] = useState(ym(new Date()));
  const [data, setData] = useState({ holidays: [], leaves: [], events: [], presentUsers: [], tasks: [] });
  const [balances, setBalances] = useState(null);
  const initialForm = { type: "Casual", start_date: "", end_date: "", reason: "" };
  const [form, setForm] = useState(initialForm);
  const [userTasksSummary, setUserTasksSummary] = useState([]);
  const [timesheetData, setTimesheetData] = useState({});
  const [googleEvents, setGoogleEvents] = useState([]);


  const refresh = async () => {
    if (!user?.id) return;

    const monthStart = format(startOfMonth(new Date(month)), "yyyy-MM-dd");
    const monthEnd = format(endOfMonth(new Date(month)), "yyyy-MM-dd");

    // Google Calendar is optional: not configured / not connected / 412 must never
    // break the rest of the calendar, so this promise can never reject and is kept
    // completely outside the Promise.all below.
    const googleEventsPromise = api
      .get(`${API}/google/events`, { params: { month }, silent: true })
      .then((x) => x.data?.events || [])
      .catch(() => []);

    try {
      const [calendar, presentUsers, tasksData, leaveDetails, userLeaveInfo, userTaskSummary] =
        await Promise.all([
          api.get(`${API}/calendar`, { params: { month } })
            .then((x) => x.data),

          api.get(`${API}/attendance/present-users-month`, { params: { month } })
            .then((x) => x.data.presentUsers || []),

          api.get(`${API}/tasks/calendar-admin`, { params: { month } })
            .then((x) => x.data.tasks || []).catch(() => []),

          api.get(`${API}/calendar/leave-details`, { params: { month } })
            .then((x) => x.data.leave_summary || []).catch(() => []),

          // 👇 Single API for balance + requests
          api.get(`${API}/calendar/user-leave-balance/${user.id}`)
            .then((x) => x.data).catch(() => null),
          api.get(`${API}/tasks/user-summary`, { params: { month } })
            .then(x => x.data.users || [])
            .catch(() => [])
        ]);

      const normalizedHolidays = (calendar.holidays || []).map(h =>
        h.month ? h.month : h
      );
      const normalizedEvents = (calendar.events || []).map(h =>
        h.month ? h.month : h
      );
      setData({

        holidays: normalizedHolidays,
        events: normalizedEvents,
        leaves: calendar.leaves || [],
        presentUsers: presentUsers || [],
        tasks: tasksData || [],
        leaveDetails: leaveDetails || [],
      });
      setUserTasksSummary(userTaskSummary);
      setBalances(userLeaveInfo);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }

    setGoogleEvents(await googleEventsPromise);
  };

  useEffect(() => {
    refresh();
  }, [month, user?.id, googleRefreshKey]);

  const submitLeave = async (e) => {
    e.preventDefault();
    if (!user?.id || !form.start_date) return;

    try {
      await api.post(`${API}/calendar/leave-requests`, {
        user_id: user.id,
        ...form,
        end_date: form.end_date || form.start_date
      });
      setForm(initialForm);
      setIsLeaveDialogOpen(false);
      await refresh();
    } catch (ex) {
      alert(ex.response?.data?.detail || "Failed to submit leave request");
    }
  };

  const calendarEvents = useMemo(() => {
    const toLocalDate = (dateStr) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()); // local midnight
    };
    const holidays = data.holidays.flatMap(h =>
      (h.dates || [h.date]).map(date => ({
        title: h.name,
        start: toLocalDate(date),
        end: toLocalDate(date),
        allDay: true,
        resource: { ...h, isHoliday: true },
      }))
    );

    const events = data.events.flatMap(ev =>
      (ev.dates || [ev.date]).map(date => ({
        title: ev.title,
        start: toLocalDate(date),
        end: toLocalDate(date),
        allDay: true,
        resource: { ...ev, isEvent: true },
      })));


    const taskBars = data.presentUsers?.flatMap(user =>
      user?.currentTasks?.map(task => {
        if (!task.due_date) return null;

        const startDate = task.start_date ? new Date(task.start_date) : new Date();
        const endDate = new Date(task.due_date);

        return {
          title: task.task_title,
          start: startDate,
          end: addDays(endDate, 1), // Add 1 day to include the due date
          allDay: true,
          resource: {
            isTask: true,
            task,
            user: user.name,
            estimate_hours: task.estimate_hours,
            project_name: task.project_name,
            priority: task.priority
          }
        };
      }).filter(Boolean) || []
    ) || [];
    // Add tasks as calendar events

    const presentUsers = data.presentUsers?.flatMap(user =>
      user?.attendanceDays?.map(day => ({
        title: user.name,
        start: new Date(day.date),
        end: new Date(day.date),
        allDay: true,
        resource: {
          ...day,
          isPresent: true,
          name: user.name,
          hoursWorked: day.hoursWorked,
          varianceFlag: day.varianceFlag,
          checkInTime: day.checkInTime,
          currentTasks: user.currentTasks
        }
      })) || []
    ) || [];


    const tasks = data.tasks.map(task => ({
      title: task.title,
      start: task.status === "Done" && task.completed_at
        ? toLocalDate(task.completed_at)
        : toLocalDate(task.date),
      end: task.status === "Done" && task.completed_at
        ? toLocalDate(task.completed_at)
        : toLocalDate(task.date),
      allDay: true,
      resource: {
        ...task,
        isTask: true,
        status: task.status
      }
    }));

    const leaveSummaries = (data.leaveDetails || []).map(item => ({
      title: `On Leave (${item.users_on_leave.map((u) => u.name).join(', ') || item.count})`,
      start: new Date(item.date),
      end: new Date(item.date),
      allDay: true,
      resource: {
        ...item,
        isLeaveSummary: true,
      }
    }));

    // Google Calendar events. All-day values arrive as 'YYYY-MM-DD' and must be read
    // as local midnight (not UTC) so they land in the right cell.
    const parseGoogleDate = (value) => {
      if (!value) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split("-").map(Number);
        return new Date(y, m - 1, d);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const google = (googleEvents || [])
      .map(ev => {
        const start = parseGoogleDate(ev.start);
        if (!start) return null;

        let end = parseGoogleDate(ev.end) || start;
        if (ev.allDay) {
          // Google's all-day end date is EXCLUSIVE, and react-big-calendar's
          // endAccessor is exclusive too (see the taskBars +1 day above). But the
          // day dialog compares dates inclusively, so land on the last instant of
          // the final covered day — that satisfies both.
          const exclusiveEnd = end > start ? end : addDays(start, 1);
          end = new Date(exclusiveEnd.getTime() - 1);
        }

        return {
          title: ev.summary || "(No title)",
          start,
          end,
          allDay: !!ev.allDay,
          resource: { ...ev, isGoogleEvent: true },
        };
      })
      .filter(Boolean);

    return [...holidays, ...events, ...presentUsers, ...taskBars, ...tasks, ...leaveSummaries, ...google];


  }, [data, googleEvents]);
  return (
    <div className="page">
      <div className="page-header">

      </div>

      {/* Leave Request Dialog */}
      <Dialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>
              Submit your leave request. It will be sent to your manager for approval.
            </DialogDescription>
          </DialogHeader>
          <form
            id="leave-form"
            onSubmit={submitLeave}
            className="grid gap-4 py-4 sm:grid-cols-2"
          >
            <div>
              <Label>Type</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Casual">Casual</SelectItem>
                  <SelectItem value="Sick">Sick</SelectItem>
                  <SelectItem value="Earned">Earned</SelectItem>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                required
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm({ ...form, start_date: e.target.value })
                }
              />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm({ ...form, end_date: e.target.value })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Reason</Label>
              <Input
                value={form.reason}
                onChange={(e) =>
                  setForm({ ...form, reason: e.target.value })
                }
              />
            </div>
          </form>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsLeaveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="leave-form">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList className="grid w-full my-5 grid-cols-3">
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="timesheet">My Timesheet</TabsTrigger>
          <TabsTrigger value="leave">Leave Balances</TabsTrigger>
        </TabsList>

        {/* TAB 1: CALENDAR VIEW */}
        <TabsContent value="calendar" className="mt-4">
          <BigCalendar events={calendarEvents} month={month} leaveDetails={data.leaveDetails} userTasksSummary={userTasksSummary} />
        </TabsContent>

        {/* TAB 2: TIMESHEET */}
        <TabsContent value="timesheet">
          <TimeAttendancePage month={month} setMonth={setMonth} />
        </TabsContent>


        <TabsContent value="leave">
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Leave Balances & Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {balances ? (
                <>
                  {/* Leave Balance Summary */}
                  <div className="flex flex-wrap gap-4 p-4 border rounded-lg bg-muted/50 mb-6">
                    {Object.entries(balances.leaveBalance || {}).map(([type, days]) => (
                      <div key={type} className="text-center">
                        <div className="text-2xl font-bold">{days}</div>
                        <div className="text-sm text-muted-foreground capitalize">
                          {type} Days
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Leave Requests Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Manager Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(balances.leaveRequests || []).map((req) => (
                        <TableRow key={req.id}>
                          <TableCell>{req.type}</TableCell>
                          <TableCell>{fmtDate(req.start_date)}</TableCell>
                          <TableCell>{fmtDate(req.end_date)}</TableCell>
                          <TableCell>{req.reason}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                req.status === "APPROVED"
                                  ? "success"
                                  : req.status === "REJECTED"
                                    ? "destructive"
                                    : "secondary"
                              }
                            >
                              {req.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {req.approval_justification || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="text-center text-muted-foreground py-4">
                  No leave information available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}

export default CalendarPage;